import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { DashboardLayout } from './DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Lock, CheckCircle, ArrowRight, Sparkles, Loader2, 
  Eye, Search, Target, Palette, Image, FileText, TrendingUp, Bot
} from 'lucide-react';

// Map step IDs to icons
const stepIcons = {
  'vision_revelation': Eye,
  'brand_audit': Search,
  'positioning_clarity': Target,
  'vision_identity': Palette,
  'brand_visualization': Image,
  'content_creation': FileText,
  'growth_strategy': TrendingUp,
  'brand_evolution': Bot
};

const PrerequisiteGate = ({ 
  toolId, 
  toolTitle,
  children 
}) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [canAccess, setCanAccess] = useState(false);
  const [prerequisiteData, setPrerequisiteData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkPrerequisites = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/prerequisites/check/${toolId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setCanAccess(response.data.can_access);
        setPrerequisiteData(response.data);
      } catch (err) {
        console.error('Error checking prerequisites:', err);
        // On error, allow access (fail-open for better UX)
        setCanAccess(true);
        setError('Erreur lors de la vérification');
      } finally {
        setLoading(false);
      }
    };

    checkPrerequisites();
  }, [token, toolId]);

  // Loading state
  if (loading) {
    return (
      <DashboardLayout title={toolTitle}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-4 animate-spin" />
            <p className="text-gray-500">Vérification en cours...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // If user can access, render the children (the actual tool)
  if (canAccess) {
    return children;
  }

  // Blocked state - show prerequisite requirements
  return (
    <DashboardLayout title={toolTitle}>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Lock Icon */}
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-transparent flex items-center justify-center mx-auto mb-8">
            <Lock className="w-12 h-12 text-[#D4AF37]" />
          </div>

          {/* Title */}
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Encore quelques étapes
          </h1>

          {/* Message */}
          <p className="text-gray-400 text-lg mb-12 max-w-lg mx-auto">
            {prerequisiteData?.message || "Cet outil sera disponible quand tu auras complété les étapes nécessaires."}
          </p>

          {/* Missing Steps */}
          <div className="bg-[#0A0A0A] border border-[#1C1C1C] rounded-2xl p-8 mb-8">
            <h2 className="text-[#E5C585] text-sm font-medium uppercase tracking-wider mb-6">
              Étapes à compléter
            </h2>
            
            <div className="space-y-4">
              {prerequisiteData?.missing_steps?.map((step, index) => {
                const IconComponent = stepIcons[step.id] || Sparkles;
                
                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 p-4 bg-[#111] border border-[#1C1C1C] rounded-xl"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-white font-medium">{step.name}</p>
                    </div>
                    <div className="w-6 h-6 rounded-full border-2 border-[#1C1C1C] flex items-center justify-center">
                      <span className="text-[#52525B] text-xs">{index + 1}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Completed Steps (if any) */}
          {prerequisiteData?.completed_steps?.length > 0 && (
            <div className="mb-8">
              <p className="text-gray-500 text-sm mb-4">
                Tu as déjà complété {prerequisiteData.completed_steps.length} étape{prerequisiteData.completed_steps.length > 1 ? 's' : ''} !
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {prerequisiteData.completed_steps.slice(0, 5).map((stepId) => {
                  const IconComponent = stepIcons[stepId] || CheckCircle;
                  return (
                    <div 
                      key={stepId}
                      className="flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full"
                    >
                      <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                      <IconComponent className="w-4 h-4 text-[#E5C585]" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA Button */}
          {prerequisiteData?.next_step && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              onClick={() => navigate(prerequisiteData.next_step.path)}
              className="btn-gold px-8 py-4 text-lg flex items-center gap-3 mx-auto"
              data-testid="continue-journey-btn"
            >
              <span>Continuer mon parcours</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          )}

          {/* Helper text */}
          <p className="text-gray-600 text-sm mt-6">
            Prochaine étape : <span className="text-[#E5C585]">{prerequisiteData?.next_step?.name}</span>
          </p>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default PrerequisiteGate;
